import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroBanner from './components/HeroBanner';
import CategoryGrid from './components/CategoryGrid';
import ProductCarousel from './components/ProductCarousel';
import QuickActions from './components/QuickActions';
import SearchSuggestions from './components/SearchSuggestions';

const HomepageDashboard = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [weeklyDeals, setWeeklyDeals] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      loadMockData();
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const loadMockData = () => {
    // Mock Featured Products
    const mockFeatured = [
      {
        id: 1,
        name: "Organic Bananas",
        price: 2.99,
        originalPrice: 3.49,
        image: "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=300&h=300&fit=crop",
        brand: "Fresh Farms",
        rating: 4.5,
        stock: 25,
        unit: "lb"
      },
      {
        id: 2,
        name: "Fresh Whole Milk",
        price: 3.29,
        image: "https://images.pexels.com/photos/236010/pexels-photo-236010.jpeg?w=300&h=300&fit=crop",
        brand: "Dairy Best",
        rating: 4.8,
        stock: 15,
        unit: "gallon"
      },
      {
        id: 3,
        name: "Artisan Sourdough Bread",
        price: 4.99,
        originalPrice: 5.99,
        image: "https://images.pixabay.com/photo/2017/06/23/23/57/bread-2434370_1280.jpg?w=300&h=300&fit=crop",
        brand: "Baker\'s Choice",
        rating: 4.7,
        stock: 8,
        unit: "loaf"
      },
      {
        id: 4,
        name: "Free Range Eggs",
        price: 4.49,
        image: "https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=300&h=300&fit=crop",
        brand: "Happy Hens",
        rating: 4.9,
        stock: 20,
        unit: "dozen"
      },
      {
        id: 5,
        name: "Organic Spinach",
        price: 2.79,
        image: "https://images.pexels.com/photos/2325843/pexels-photo-2325843.jpeg?w=300&h=300&fit=crop",
        brand: "Green Valley",
        rating: 4.3,
        stock: 12,
        unit: "bunch"
      }
    ];

    // Mock Weekly Deals
    const mockDeals = [
      {
        id: 6,
        name: "Premium Ground Coffee",
        price: 8.99,
        originalPrice: 12.99,
        image: "https://images.pixabay.com/photo/2015/05/07/13/46/coffee-756490_1280.jpg?w=300&h=300&fit=crop",
        brand: "Mountain Roast",
        rating: 4.6,
        stock: 30,
        unit: "bag"
      },
      {
        id: 7,
        name: "Greek Yogurt Variety Pack",
        price: 5.99,
        originalPrice: 7.99,
        image: "https://images.unsplash.com/photo-1488477181946-6428a0291777?w=300&h=300&fit=crop",
        brand: "Mediterranean",
        rating: 4.4,
        stock: 18,
        unit: "pack"
      },
      {
        id: 8,
        name: "Organic Pasta Sauce",
        price: 3.49,
        originalPrice: 4.99,
        image: "https://images.pexels.com/photos/4518843/pexels-photo-4518843.jpeg?w=300&h=300&fit=crop",
        brand: "Nonna\'s Kitchen",
        rating: 4.5,
        stock: 22,
        unit: "jar"
      },
      {
        id: 9,
        name: "Wild Caught Salmon",
        price: 12.99,
        originalPrice: 16.99,
        image: "https://images.pixabay.com/photo/2014/11/05/15/57/salmon-518032_1280.jpg?w=300&h=300&fit=crop",
        brand: "Ocean Fresh",
        rating: 4.8,
        stock: 6,
        unit: "lb"
      }
    ];

    // Mock Recently Viewed (from localStorage simulation)
    const mockRecentlyViewed = [
      {
        id: 10,
        name: "Avocados",
        price: 1.99,
        image: "https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=300&h=300&fit=crop",
        brand: "Fresh Select",
        rating: 4.2,
        stock: 35,
        unit: "each"
      },
      {
        id: 11,
        name: "Quinoa",
        price: 6.99,
        image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?w=300&h=300&fit=crop",
        brand: "Ancient Grains",
        rating: 4.7,
        stock: 14,
        unit: "bag"
      },
      {
        id: 12,
        name: "Almond Milk",
        price: 3.79,
        image: "https://images.pixabay.com/photo/2017/06/02/18/24/almond-milk-2367416_1280.jpg?w=300&h=300&fit=crop",
        brand: "Nutty Delight",
        rating: 4.1,
        stock: 28,
        unit: "carton"
      }
    ];

    // Mock Recommended Products
    const mockRecommended = [
      {
        id: 13,
        name: "Organic Chicken Breast",
        price: 9.99,
        image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=300&h=300&fit=crop",
        brand: "Farm Fresh",
        rating: 4.6,
        stock: 12,
        unit: "lb"
      },
      {
        id: 14,
        name: "Mixed Berry Smoothie Pack",
        price: 4.99,
        originalPrice: 6.49,
        image: "https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg?w=300&h=300&fit=crop",
        brand: "Frozen Delights",
        rating: 4.3,
        stock: 20,
        unit: "bag"
      },
      {
        id: 15,
        name: "Artisan Cheese Selection",
        price: 11.99,
        image: "https://images.pixabay.com/photo/2016/12/26/17/28/spaghetti-1932466_1280.jpg?w=300&h=300&fit=crop",
        brand: "Gourmet Dairy",
        rating: 4.9,
        stock: 8,
        unit: "pack"
      }
    ];

    setFeaturedProducts(mockFeatured);
    setWeeklyDeals(mockDeals);
    setRecentlyViewed(mockRecentlyViewed);
    setRecommendedProducts(mockRecommended);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Loading Skeleton */}
            <div className="animate-pulse">
              <div className="h-64 md:h-80 lg:h-96 bg-border-light rounded-card mb-8"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-32 bg-border-light rounded-card"></div>
                ))}
              </div>
              <div className="space-y-8">
                {[...Array(3)].map((_, i) => (
                  <div key={i}>
                    <div className="h-8 bg-border-light rounded w-48 mb-4"></div>
                    <div className="flex space-x-4">
                      {[...Array(4)].map((_, j) => (
                        <div key={j} className="w-64 h-80 bg-border-light rounded-card flex-shrink-0"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>FreshCart - Fresh Groceries Delivered to Your Door</title>
        <meta name="description" content="Shop fresh groceries online with FreshCart. Get farm-fresh produce, dairy, meat, and more delivered to your doorstep. Fast delivery, great prices." />
        <meta name="keywords" content="grocery delivery, fresh produce, online grocery shopping, food delivery" />
      </Helmet>

      <Header />
      
      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Hero Banner */}
          <HeroBanner />

          {/* Category Grid */}
          <CategoryGrid />

          {/* Featured Products */}
          <ProductCarousel
            title="Featured Products"
            products={featuredProducts}
            viewAllLink="/product-catalog-category-browse?filter=featured"
          />

          {/* Weekly Deals */}
          <ProductCarousel
            title="Weekly Deals"
            products={weeklyDeals}
            viewAllLink="/product-catalog-category-browse?category=deals"
          />

          {/* Quick Actions */}
          <QuickActions />

          {/* Recently Viewed */}
          {recentlyViewed.length > 0 && (
            <ProductCarousel
              title="Recently Viewed"
              products={recentlyViewed}
              showViewAll={false}
            />
          )}

          {/* Search Suggestions */}
          <SearchSuggestions />

          {/* Recommended Products */}
          <ProductCarousel
            title="Recommended for You"
            products={recommendedProducts}
            viewAllLink="/product-catalog-category-browse?filter=recommended"
          />
        </div>
      </main>
    </div>
  );
};

export default HomepageDashboard;